import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import { X, Copy, Check, Package, Loader, RefreshCw, Send, AlertTriangle, FileText, DollarSign, ClipboardList } from 'lucide-react'

import { C } from './theme'
import { lerTudo } from './lerTudo'

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '—'
const fmtEur = (n) => Number(n || 0).toFixed(2) + ' €'

// Os escalões deixaram de viver aqui.
//
// Estavam quatro constantes neste sítio — 100 un de mínimo, 200 un de escalão,
// +2%, 10% de desconto base — e aplicavam-se a TODAS as encomendas. São as
// condições da Plus Skill, e no dia em que entrou um segundo fornecedor
// passaram a ser aplicadas a encomendas dele, que não tem escalões nenhuns:
// o mínimo dele é em euros, não em unidades.
//
// Agora vêm de `fornecedores`, por fornecedor. Nulo = sem escalões, e nesse
// caso não há mínimo de unidades, nem barra, nem avisos — não há nada por que
// esperar. Um fornecedor novo entra preenchendo a ficha, sem tocar em código.
//
// Fechar um pedido a 190 un custa dinheiro real e o erro só se nota na factura,
// quando já não há volta. É por isso que o aviso vive no botão de fechar.
const REGRAS_SEM_ESCALAO = { nome: null, min: 0, escalaoUn: 0, escalaoEur: 0, escalaoPct: 0, descontoBase: 0 }

const regrasDoFornecedor = (f) => f ? {
  nome:         f.nome || null,
  min:          Number(f.min_unidades_pedido) || 0,
  escalaoUn:    Number(f.escalao_unidades) || 0,
  escalaoEur:   Number(f.escalao_valor_eur) || 0,
  escalaoPct:   Number(f.escalao_pct) || 0,
  descontoBase: Number(f.desconto_base_pct) || 0,
} : REGRAS_SEM_ESCALAO

// O custo que temos guardado já traz o desconto base aplicado
// (preco_custo = bruto × (1 − desconto)). Para saber o que valem os pontos
// extra do escalão é preciso voltar ao bruto. Estimativa: há artigos com
// desconto diferente do base.
// Onde é que este pedido está em relação ao escalão, na unidade em que o
// fornecedor o mede. A Plus Skill conta unidades (200 un → +2%); o Bplatz
// conta euros (5.000 € → +3%).
//
// Sem isto, um fornecedor com escalão em euros não disparava aviso nenhum:
// fechava-se um pedido de 4.800 € a 200 de distância dos 3% e o erro só
// aparecia na factura, quando já não há volta. É precisamente o que este ecrã
// existe para evitar.
const estadoEscalao = (regras, un, custo) => {
  if (!(regras.escalaoPct > 0)) return null
  if (regras.escalaoUn > 0) return {
    atingido: un >= regras.escalaoUn,
    falta: `${regras.escalaoUn - un} un`,
    alvo:  `${regras.escalaoUn} un`,
    tem:   `${un} un`,
  }
  if (regras.escalaoEur > 0) return {
    atingido: custo >= regras.escalaoEur,
    falta: fmtEur(regras.escalaoEur - custo),
    alvo:  fmtEur(regras.escalaoEur),
    tem:   fmtEur(custo),
  }
  return null
}

const pouparNoEscalao = (custoComDescontoBase, regras) => {
  if (!(regras.escalaoPct > 0)) return 0
  const div = 1 - (regras.descontoBase / 100)
  const bruto = Number(custoComDescontoBase || 0) / (div > 0 ? div : 1)
  return bruto * (regras.escalaoPct / 100)
}

// Gera o texto formatado para colar no email do fornecedor (SEM CUSTOS)
function montarTexto(enc, itens) {
  const numero = enc.numero_encomenda || enc.id.slice(0, 8).toUpperCase()
  const cliente = enc.cliente_nome || enc.cliente_email
  const data = fmtDate(enc.created_at)
  const linhas = [
    `Encomenda ${numero} — ${cliente} — ${data}`,
    '',
  ]
  itens.forEach(it => {
    const ean = it.produto_ean || '—'
    linhas.push(`${it.quantidade}x ${ean} — ${it.produto_nome}`)
  })
  const totalUn = itens.reduce((s, it) => s + Number(it.quantidade || 0), 0)
  linhas.push('')
  linhas.push(`Total: ${totalUn} unidades`)
  return linhas.join('\n')
}

// Custo de um item: usa EAN primeiro (mais robusto), fallback nome
function custoItem(it, precoCustoPorEan, precoCustoPorNome) {
  const ean = it.produto_ean ? String(it.produto_ean) : ''
  if (ean && precoCustoPorEan[ean] != null) return Number(precoCustoPorEan[ean])
  if (it.produto_nome && precoCustoPorNome[it.produto_nome] != null) return Number(precoCustoPorNome[it.produto_nome])
  return null
}

function estadoEncomenda(enc, facturaMap) {
  if (enc.fornecedor_fatura_numero) {
    const factura = facturaMap[enc.fornecedor_fatura_numero]
    return { tipo: 'encomendada', factura }
  }
  if (enc.encomendado_fornecedor_em) {
    return { tipo: 'enviado', em: enc.encomendado_fornecedor_em }
  }
  return { tipo: 'falta' }
}

export default function PedidosFornecedor({ onClose }) {
  const [encomendas, setEncomendas] = useState([])
  const [itensPorEnc, setItensPorEnc] = useState({})
  const [facturaMap, setFacturaMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [copiadoId, setCopiadoId] = useState(null)
  const [seleccionadas, setSeleccionadas] = useState(new Set())
  const [enviando, setEnviando] = useState(false)
  const [resultadoEnvio, setResultadoEnvio] = useState(null)
  const [copiouResumoAdmin, setCopiouResumoAdmin] = useState(false)

  // Mapas de custos (INTERNO - nunca vai para email/fornecedor)
  const [precoCustoPorEan, setPrecoCustoPorEan] = useState({})
  const [precoCustoPorNome, setPrecoCustoPorNome] = useState({})
  // Marca do acordo do sinal reduzido, por nome de produto — decide se a 1.ª ao
  // fornecedor sao 20% ou 50%. Mesma regra do carrinho e da importacao da
  // factura: so 20% se TODOS os artigos do pedido estiverem marcados.
  const [flexPorNome, setFlexPorNome] = useState({})
  // Fornecedores com os escaloes de cada um. Estavam em constantes no topo
  // deste ficheiro, com os valores da Plus Skill, e aplicavam-se a tudo.
  const [fornecedores, setFornecedores] = useState([])
  const [fornecedorFiltro, setFornecedorFiltro] = useState(null)
  useEffect(() => {
    let cancelado = false
    ;(async () => {
      // Paginado: sao mais de 2.700 artigos e a leitura simples parava nas
      // primeiras 1000 — os custos dos restantes ficavam por saber e o pedido
      // ao fornecedor era fechado com um total mais baixo do que o real.
      const [{ data: int }, { data: prod }, { data: forns }] = await Promise.all([
        lerTudo(() => supabase.from('produtos_internos').select('produto_nome, preco_custo'), 'produto_nome'),
        lerTudo(() => supabase.from('Produtos').select('id, Nome, EAN, sinal_flexivel'), 'id'),
        supabase.from('fornecedores')
          .select('id, nome, min_unidades_pedido, escalao_unidades, escalao_valor_eur, escalao_pct, desconto_base_pct')
          .eq('ativo', true).order('nome'),
      ])
      if (cancelado) return
      const porNome = {}
      ;(int || []).forEach(r => { if (r.preco_custo != null) porNome[r.produto_nome] = Number(r.preco_custo) })
      const porEan = {}
      ;(prod || []).forEach(p => {
        if (p.EAN && porNome[p.Nome] != null) porEan[String(p.EAN)] = porNome[p.Nome]
      })
      setPrecoCustoPorNome(porNome)
      setPrecoCustoPorEan(porEan)
      setFornecedores(forns || [])
      const flex = {}
      ;(prod || []).forEach(p => { flex[p.Nome] = p.sinal_flexivel === true })
      setFlexPorNome(flex)
    })()
    return () => { cancelado = true }
  }, [])

  const carregar = async () => {
    setLoading(true)
    setResultadoEnvio(null)
    // Encomendas com 1ª prestação paga OU promovidas pelo admin (sem pagamento)
    const { data: encs } = await supabase
      .from('encomendas')
      // fornecedor_id: gravado na encomenda pela RPC. E o que permite fazer um
      // pedido POR fornecedor em vez de um so com tudo a mistura.
      .select('id, numero_encomenda, cliente_nome, cliente_email, created_at, estado, pago_1a_prestacao, pago_2a_prestacao, encomendado_fornecedor_em, fornecedor_fatura_numero, promovido_pedido_em, promovido_pedido_nota, historica, fornecedor_id')
      .or('pago_1a_prestacao.eq.true,promovido_pedido_em.not.is.null')
      .not('estado', 'in', '(cancelado,concluido)')
      // Registos historicos (anteriores ao sistema) nunca entram: ja foram
      // comprados ao fornecedor ha muito e so inflacionavam as unidades a pedir.
      .eq('historica', false)
      .order('created_at', { ascending: false })
    const lista = encs || []
    setEncomendas(lista)

    if (lista.length > 0) {
      const ids = lista.map(e => e.id)
      const { data: items } = await supabase
        .from('encomenda_itens')
        .select('encomenda_id, produto_nome, produto_ean, quantidade')
        .in('encomenda_id', ids)
        .order('produto_nome')
      const grupos = {}
      ;(items || []).forEach(it => {
        if (!grupos[it.encomenda_id]) grupos[it.encomenda_id] = []
        grupos[it.encomenda_id].push(it)
      })
      setItensPorEnc(grupos)

      // Cruzar com compras_fornecedor para obter dados de factura
      const numerosFactura = [...new Set(lista.map(e => e.fornecedor_fatura_numero).filter(Boolean))]
      if (numerosFactura.length > 0) {
        const { data: comps } = await supabase
          .from('compras_fornecedor')
          .select('numero_fatura, numero_fatura_final, data_fatura, data_fatura_final')
          .or(`numero_fatura.in.(${numerosFactura.map(n => `"${n}"`).join(',')}),numero_fatura_final.in.(${numerosFactura.map(n => `"${n}"`).join(',')})`)
        const mapa = {}
        ;(comps || []).forEach(c => {
          if (c.numero_fatura) mapa[c.numero_fatura] = c
          if (c.numero_fatura_final) mapa[c.numero_fatura_final] = c
        })
        setFacturaMap(mapa)
      } else {
        setFacturaMap({})
      }
    } else {
      setItensPorEnc({})
      setFacturaMap({})
    }
    setLoading(false)
  }

  useEffect(() => { carregar() }, [])

  const copiar = async (encId, texto) => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiadoId(encId)
      setTimeout(() => setCopiadoId(null), 2000)
    } catch (e) {
      alert('Não foi possível copiar — selecciona o texto manualmente.')
    }
  }

  // ─── Um pedido por fornecedor ──────────────────────────────────────────────
  // O ecrã mostra um fornecedor de cada vez. Não é cosmético: cada um tem o seu
  // mínimo, o seu escalão e o seu email, e um pedido com artigos de dois não
  // existe — teria de ser partido em dois na mesma. Filtrar aqui garante que a
  // selecção nunca mistura, sem precisar de a policiar a cada clique.
  const encomendasDoFornecedor = useMemo(() => {
    if (!fornecedorFiltro) return encomendas
    return encomendas.filter(e => String(e.fornecedor_id) === String(fornecedorFiltro))
  }, [encomendas, fornecedorFiltro])

  // Regras em vigor = as do fornecedor que está a ser mostrado.
  const regras = useMemo(
    () => regrasDoFornecedor(fornecedores.find(f => String(f.id) === String(fornecedorFiltro))),
    [fornecedores, fornecedorFiltro])

  // Quantas encomendas por fornecedor, para o selector e para escolher o
  // primeiro a mostrar.
  const contagemPorForn = useMemo(() => {
    const m = new Map()
    for (const e of encomendas) {
      if (estadoEncomenda(e, facturaMap).tipo !== 'falta') continue
      const k = String(e.fornecedor_id || 'sem')
      m.set(k, (m.get(k) || 0) + 1)
    }
    return m
  }, [encomendas, facturaMap])

  // Abre no fornecedor que tem mais encomendas por pedir.
  useEffect(() => {
    if (fornecedorFiltro || fornecedores.length === 0 || encomendas.length === 0) return
    let melhor = null, melhorN = -1
    for (const f of fornecedores) {
      const n = contagemPorForn.get(String(f.id)) || 0
      if (n > melhorN) { melhorN = n; melhor = f.id }
    }
    if (melhor) setFornecedorFiltro(melhor)
  }, [fornecedores, encomendas, contagemPorForn, fornecedorFiltro])

  // Ao trocar de fornecedor a selecção tem de ir abaixo, senão ficavam ticadas
  // encomendas de outro que já nem estão na lista.
  useEffect(() => { setSeleccionadas(new Set()) }, [fornecedorFiltro])

  // Lista de encomendas em estado FALTA (só do fornecedor mostrado)
  const idsFalta = useMemo(() => {
    return encomendasDoFornecedor
      .filter(e => estadoEncomenda(e, facturaMap).tipo === 'falta')
      .map(e => e.id)
  }, [encomendasDoFornecedor, facturaMap])

  const toggleSel = (id) => {
    setSeleccionadas(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleTodas = () => {
    if (seleccionadas.size === idsFalta.length) setSeleccionadas(new Set())
    else setSeleccionadas(new Set(idsFalta))
  }

  const criarPedido = async () => {
    if (seleccionadas.size === 0) return

    const un = totalSelUnidades

    // Abaixo do mínimo o fornecedor nem aceita o pedido — não é um aviso, é um
    // travão. Só existe em quem tem mínimo de unidades definido na ficha.
    if (regras.min > 0 && un < regras.min) {
      alert(
        `Só tens ${un} unidades seleccionadas.\n\n` +
        `O mínimo deste fornecedor são ${regras.min} unidades — faltam ${regras.min - un}.\n\n` +
        `Junta mais encomendas antes de fechar o pedido.`
      )
      return
    }

    // Entre o mínimo e o escalão: dá para fechar, mas fica dinheiro em cima da mesa.
    const esc = estadoEscalao(regras, un, totalSelCusto.custo)
    if (esc && !esc.atingido) {
      const perde = pouparNoEscalao(totalSelCusto.custo, regras)
      const ok = confirm(
        `⚠ Estás a ${esc.falta} do escalão de ${esc.alvo}.\n\n` +
        `Tens ${esc.tem}. A partir de ${esc.alvo} o fornecedor dá mais ${regras.escalaoPct}% ` +
        `além dos ${regras.descontoBase}% habituais.\n\n` +
        `Fechar agora custa-te cerca de ${fmtEur(perde)} neste pedido.\n\n` +
        `Queres mesmo fechar já, ou esperar por mais ${esc.falta}?\n\n` +
        `OK = fechar agora · Cancelar = esperar`
      )
      if (!ok) return
    }

    const nota = (esc && esc.atingido)
      ? `\n\n✓ ${esc.tem} — passa ${esc.alvo}, ficas com ${regras.descontoBase}% + ${regras.escalaoPct}%.`
      : ''
    if (!confirm(`Criar pedido ao fornecedor com ${seleccionadas.size} encomenda(s)?${nota}\n\nO email será enviado para comercial@lognow.pt para revisão antes de seguir ao fornecedor.`)) return
    setEnviando(true)
    setResultadoEnvio(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) {
        alert('Sessão expirada. Inicia sessão novamente.')
        return
      }
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://omutjrewscipmnpkoedk.supabase.co'
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/enviar-pedido-fornecedor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ encomenda_ids: [...seleccionadas] }),
      })
      const result = await resp.json()
      if (!resp.ok || !result.ok) {
        setResultadoEnvio({ ok: false, erro: result.erro || `HTTP ${resp.status}` })
        return
      }
      setResultadoEnvio({ ok: true, ...result })
      setSeleccionadas(new Set())
      await carregar()
    } catch (e) {
      setResultadoEnvio({ ok: false, erro: String(e?.message || e) })
    } finally {
      setEnviando(false)
    }
  }

  const totalSelUnidades = useMemo(() => {
    let n = 0
    for (const id of seleccionadas) {
      const itens = itensPorEnc[id] || []
      n += itens.reduce((s, it) => s + Number(it.quantidade || 0), 0)
    }
    return n
  }, [seleccionadas, itensPorEnc])

  // Custo por encomenda + flag se algum item sem preco_custo (INTERNO)
  const custoPorEnc = useMemo(() => {
    const m = {}
    for (const enc of encomendas) {
      const itens = itensPorEnc[enc.id] || []
      let custo = 0
      let semCustoCount = 0
      for (const it of itens) {
        const c = custoItem(it, precoCustoPorEan, precoCustoPorNome)
        if (c == null) { semCustoCount++; continue }
        custo += c * Number(it.quantidade || 0)
      }
      m[enc.id] = { custo, semCustoCount, totalItens: itens.length }
    }
    return m
  }, [encomendas, itensPorEnc, precoCustoPorEan, precoCustoPorNome])

  const totalSelCusto = useMemo(() => {
    let c = 0
    let semCustoTotal = 0
    for (const id of seleccionadas) {
      const info = custoPorEnc[id]
      if (info) {
        c += info.custo
        semCustoTotal += info.semCustoCount
      }
    }
    // Percentagem da 1.ª ao fornecedor: 20% se TODOS os artigos seleccionados
    // estiverem no acordo, senao os 50% do costume. Mesma regra do carrinho e da
    // importacao da factura — basta um artigo de fora para nao ser.
    const nomes = []
    for (const id of seleccionadas) (itensPorEnc[id] || []).forEach(it => nomes.push(it.produto_nome))
    const todosNoAcordo = nomes.length > 0 && nomes.every(n => flexPorNome[n] === true)
    const pct = todosNoAcordo ? 20 : 50
    return {
      custo: c, semCusto: semCustoTotal, pctForn: pct, todosNoAcordo,
      prestacao_1a: Math.round(c * pct) / 100,
      prestacao_2a: Math.round(c * (100 - pct)) / 100,
    }
  }, [seleccionadas, custoPorEnc, itensPorEnc, flexPorNome])

  // Texto detalhado COM custos para colar nos teus registos (NUNCA enviado ao fornecedor)
  const resumoAdminTexto = useMemo(() => {
    if (seleccionadas.size === 0) return ''
    // Agrupar por cliente
    const porCliente = new Map()
    for (const id of seleccionadas) {
      const enc = encomendas.find(e => e.id === id)
      if (!enc) continue
      const cliente = enc.cliente_nome || enc.cliente_email
      if (!porCliente.has(cliente)) porCliente.set(cliente, [])
      porCliente.get(cliente).push(enc)
    }
    const linhas = []
    // O cabeçalho leva o nome do fornecedor a quem o pedido vai. Estava
    // "PEDIDO PLUS SKILL" cravado, e um pedido ao segundo fornecedor saía com
    // o nome do primeiro no topo.
    linhas.push(`=== PEDIDO ${(regras.nome || 'FORNECEDOR').toUpperCase()} · ${fmtDate(new Date().toISOString())} ===`)
    linhas.push('')
    let custoGeral = 0
    let unGeral = 0
    for (const [cliente, encs] of porCliente) {
      let subCliente = 0
      let unCliente = 0
      const linhasCliente = []
      for (const enc of encs) {
        const itens = itensPorEnc[enc.id] || []
        const numero = enc.numero_encomenda || enc.id.slice(0, 8).toUpperCase()
        linhasCliente.push(`${cliente} (${numero}):`)
        let subEnc = 0
        for (const it of itens) {
          const custoUn = custoItem(it, precoCustoPorEan, precoCustoPorNome)
          const qty = Number(it.quantidade || 0)
          unCliente += qty
          unGeral += qty
          if (custoUn != null) {
            const linha = qty * custoUn
            subEnc += linha
            linhasCliente.push(`  ${qty} × ${it.produto_nome} @ ${fmtEur(custoUn)} = ${fmtEur(linha)}`)
          } else {
            linhasCliente.push(`  ${qty} × ${it.produto_nome}  ⚠ SEM PRECO CUSTO`)
          }
        }
        linhasCliente.push(`  Subtotal encomenda: ${fmtEur(subEnc)}`)
        linhasCliente.push('')
        subCliente += subEnc
      }
      linhas.push(...linhasCliente)
      linhas.push(`Subtotal ${cliente}: ${fmtEur(subCliente)} (${unCliente} un)`)
      linhas.push('')
      custoGeral += subCliente
    }
    linhas.push('=== TOTAIS ===')
    linhas.push(`${seleccionadas.size} encomenda(s) · ${unGeral} unidades`)
    linhas.push(`Custo total: ${fmtEur(custoGeral)}`)
    linhas.push(`1.ª prestação (${totalSelCusto.pctForn}%): ${fmtEur(custoGeral * totalSelCusto.pctForn / 100)}`)
    linhas.push(`2.ª prestação (${100 - totalSelCusto.pctForn}%): ${fmtEur(custoGeral * (100 - totalSelCusto.pctForn) / 100)}`)
    if (unGeral < 100) linhas.push(`⚠ ABAIXO DO MINIMO 100 UN (faltam ${100 - unGeral})`)
    return linhas.join('\n')
  }, [seleccionadas, encomendas, itensPorEnc, precoCustoPorEan, precoCustoPorNome, totalSelCusto])

  const copiarResumoAdmin = async () => {
    if (!resumoAdminTexto) return
    try {
      await navigator.clipboard.writeText(resumoAdminTexto)
      setCopiouResumoAdmin(true)
      setTimeout(() => setCopiouResumoAdmin(false), 2000)
    } catch {
      alert('Nao foi possivel copiar — selecciona o texto manualmente.')
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div style={{ backgroundColor: C.surface, borderRadius: '6px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '760px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>

        {/* Cabeçalho */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, backgroundColor: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Package size={18} color={C.primary} />
            <div>
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: C.text, margin: 0 }}>Pedidos a Fornecedor</h2>
              <p style={{ fontSize: '11px', color: C.textMuted, margin: 0 }}>
                Selecciona as pendentes e cria pedido consolidado por email
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={carregar} disabled={loading} title="Atualizar"
              style={{ height: '32px', width: '32px', border: `1px solid ${C.border}`, borderRadius: '3px', backgroundColor: C.surface, cursor: loading ? 'wait' : 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RefreshCw size={13} />
            </button>
            <button onClick={onClose} title="Fechar"
              style={{ height: '32px', width: '32px', border: `1px solid ${C.border}`, borderRadius: '3px', backgroundColor: C.surface, cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Selector de fornecedor — só aparece quando há mais do que um.
            Um pedido é sempre a UM fornecedor: cada um tem o seu mínimo, o seu
            escalão e o seu email. Ver um de cada vez impede a mistura à nascença. */}
        {!loading && fornecedores.length > 1 && (
          <div style={{ padding: '8px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pedido a</span>
            {fornecedores.map(f => {
              const activo = String(f.id) === String(fornecedorFiltro)
              const n = contagemPorForn.get(String(f.id)) || 0
              return (
                <button key={f.id} onClick={() => setFornecedorFiltro(f.id)}
                  style={{ padding: '5px 12px', border: `1px solid ${activo ? C.primary : C.border}`, borderRadius: '14px', background: activo ? C.primary : C.surface, color: activo ? '#fff' : C.text, cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                  {f.nome}{n > 0 ? ` · ${n}` : ''}
                </button>
              )
            })}
            {(contagemPorForn.get('sem') || 0) > 0 && (
              <button onClick={() => setFornecedorFiltro('sem')}
                title="Encomendas antigas cujos artigos não têm fornecedor definido"
                style={{ padding: '5px 12px', border: `1px solid ${String(fornecedorFiltro) === 'sem' ? C.warning : C.border}`, borderRadius: '14px', background: String(fornecedorFiltro) === 'sem' ? C.warning : C.surface, color: String(fornecedorFiltro) === 'sem' ? '#fff' : C.textMuted, cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                Sem fornecedor · {contagemPorForn.get('sem')}
              </button>
            )}
          </div>
        )}

        {/* Barra de acção (selecção) */}
        {!loading && idsFalta.length > 0 && (
          <div style={{ padding: '10px 18px', borderBottom: `1px solid ${C.border}`, backgroundColor: '#fff8e1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button onClick={toggleTodas}
                style={{ padding: '6px 10px', border: `1px solid ${C.border}`, borderRadius: '3px', backgroundColor: C.surface, cursor: 'pointer', fontSize: '11px', fontWeight: '600', color: C.text }}>
                {seleccionadas.size === idsFalta.length ? 'Limpar selecção' : `Seleccionar todas (${idsFalta.length})`}
              </button>
              <span style={{ fontSize: '12px', color: C.textMuted }}>
                {seleccionadas.size > 0
                  ? <><strong style={{ color: C.text }}>{seleccionadas.size}</strong> seleccionada(s) · <strong style={{ color: C.text }}>{totalSelUnidades} un</strong></>
                  : `${idsFalta.length} por encomendar ao fornecedor`}
              </span>
              {/* Onde está o pedido face aos escalões — em euros, que é o que decide */}
              {/* Sem escalões definidos na ficha não há distintivo nenhum: não
                  há mínimo de unidades por atingir nem desconto por que esperar. */}
              {seleccionadas.size > 0 && (regras.min > 0 || regras.escalaoPct > 0) && (() => {
                const un = totalSelUnidades
                if (regras.min > 0 && un < regras.min) return (
                  <span style={{ fontSize: '11px', fontWeight: '800', color: C.danger, background: C.dangerSoft, border: `1px solid ${C.danger}55`, borderRadius: '10px', padding: '3px 9px' }}>
                    ✕ Faltam {regras.min - un} un para o mínimo de {regras.min}
                  </span>
                )
                const esc = estadoEscalao(regras, un, totalSelCusto.custo)
                if (esc && !esc.atingido) return (
                  <span title={`A partir de ${esc.alvo} o fornecedor dá mais ${regras.escalaoPct}% além dos ${regras.descontoBase}% habituais. Valor estimado a partir do custo já com o desconto base.`}
                    style={{ fontSize: '11px', fontWeight: '800', color: '#92400e', background: '#fff4ce', border: '1px solid #c0740055', borderRadius: '10px', padding: '3px 9px' }}>
                    ⚠ Faltam {esc.falta} para os +{regras.escalaoPct}% — valem ≈ {fmtEur(pouparNoEscalao(totalSelCusto.custo, regras))}
                  </span>
                )
                if (esc) return (
                  <span style={{ fontSize: '11px', fontWeight: '800', color: C.success, background: C.successSoft, border: `1px solid ${C.success}55`, borderRadius: '10px', padding: '3px 9px' }}>
                    ✓ Passa {esc.alvo} — {regras.descontoBase}% + {regras.escalaoPct}%
                  </span>
                )
                return null
              })()}
            </div>
            <button onClick={criarPedido} disabled={seleccionadas.size === 0 || enviando}
              style={{ padding: '8px 14px', border: 'none', borderRadius: '3px', backgroundColor: seleccionadas.size > 0 ? C.primary : C.borderStrong, color: '#fff', cursor: seleccionadas.size > 0 && !enviando ? 'pointer' : 'not-allowed', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {enviando ? <><Loader size={13} /> A enviar…</> : <><Send size={13} /> Criar pedido ao fornecedor</>}
            </button>
          </div>
        )}

        {/* Card de custos INTERNO (apenas visível ao admin no ecrã - nunca exportado) */}
        {!loading && seleccionadas.size > 0 && (
          <div style={{ padding: '10px 18px', borderBottom: `1px solid ${C.border}`, background: '#0a0a0a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <DollarSign size={14} color="#FACC15" />
                <span style={{ fontSize: '10px', color: '#FACC15', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Custo INTERNO (não vai ao fornecedor)</span>
              </div>
              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', fontSize: '12px' }}>
                <div><span style={{ color: '#a1a1aa' }}>Custo:</span> <strong style={{ fontSize: '14px' }}>{fmtEur(totalSelCusto.custo)}</strong></div>
                <div><span style={{ color: '#a1a1aa' }}>1.ª ({totalSelCusto.pctForn}%):</span> <strong style={{ color: '#FACC15' }}>{fmtEur(totalSelCusto.prestacao_1a)}</strong></div>
                <div><span style={{ color: '#a1a1aa' }}>2.ª ({100 - totalSelCusto.pctForn}%):</span> <strong>{fmtEur(totalSelCusto.prestacao_2a)}</strong></div>
                {totalSelUnidades < 100 && (
                  <div style={{ background: '#7f1d1d', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>
                    ⚠ {100 - totalSelUnidades} un abaixo do mínimo
                  </div>
                )}
                {totalSelCusto.semCusto > 0 && (
                  <div style={{ background: '#92400e', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>
                    ⚠ {totalSelCusto.semCusto} item(s) sem preço custo
                  </div>
                )}
              </div>
            </div>
            <button onClick={copiarResumoAdmin} disabled={!resumoAdminTexto}
              style={{ padding: '6px 12px', border: 'none', borderRadius: '3px', backgroundColor: copiouResumoAdmin ? C.success : '#FACC15', color: copiouResumoAdmin ? '#fff' : '#0a0a0a', cursor: 'pointer', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
              {copiouResumoAdmin ? <><Check size={12} /> Copiado</> : <><ClipboardList size={12} /> Copiar resumo admin</>}
            </button>
          </div>
        )}

        {/* Resultado envio */}
        {resultadoEnvio && (
          <div style={{ padding: '10px 18px', borderBottom: `1px solid ${C.border}`, backgroundColor: resultadoEnvio.ok ? (resultadoEnvio.ignoradas?.length ? '#fff4ce' : '#dff6dd') : '#fde7e9', fontSize: '12px', color: resultadoEnvio.ok ? (resultadoEnvio.ignoradas?.length ? '#92400e' : '#107c10') : '#a4262c', display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
            {resultadoEnvio.ok
              ? <>
                  <Check size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    Email enviado para <strong>{resultadoEnvio.para}</strong> · {resultadoEnvio.encomendas_marcadas} encomenda(s) marcada(s) · {resultadoEnvio.total_unidades} un. Revê e reencaminha ao fornecedor.
                    {/* O que nao seguiu tem de ser dito — antes desaparecia calado */}
                    {resultadoEnvio.ignoradas?.length > 0 && (
                      <span style={{ display: 'block', marginTop: '4px', fontWeight: '700' }}>
                        ⚠ Ficaram de fora {resultadoEnvio.ignoradas.length}:{' '}
                        {resultadoEnvio.ignoradas.map(i => `${i.numero} (${i.motivo})`).join(' · ')}
                      </span>
                    )}
                  </span>
                </>
              : <><AlertTriangle size={14} /> Erro: {resultadoEnvio.erro}</>
            }
          </div>
        )}

        {/* Conteúdo */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: C.textMuted, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Loader size={14} /> A carregar…
            </div>
          ) : encomendasDoFornecedor.length === 0 ? (
            <div style={{ padding: '40px 18px', textAlign: 'center', color: C.textMuted, fontSize: '13px', border: `1px dashed ${C.border}`, borderRadius: '4px' }}>
              {encomendas.length === 0
                ? 'Sem encomendas com 1.ª prestação paga ainda.'
                : 'Sem encomendas por pedir a este fornecedor.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {encomendasDoFornecedor.map(enc => {
                const itens = itensPorEnc[enc.id] || []
                const texto = montarTexto(enc, itens)
                const numero = enc.numero_encomenda || enc.id.slice(0, 8).toUpperCase()
                const cliente = enc.cliente_nome || enc.cliente_email
                const totalUn = itens.reduce((s, it) => s + Number(it.quantidade || 0), 0)
                const foiCopiado = copiadoId === enc.id
                const estado = estadoEncomenda(enc, facturaMap)
                const podeSeleccionar = estado.tipo === 'falta'
                const sel = seleccionadas.has(enc.id)
                // Promovida sem pagamento: encomenda nao tem 1a paga mas admin promoveu
                const isPromovidaSemPagamento = !enc.pago_1a_prestacao && !!enc.promovido_pedido_em

                let badgePromocao = null
                if (isPromovidaSemPagamento) {
                  badgePromocao = (
                    <div style={{ padding: '8px 12px', backgroundColor: '#fee2e2', borderLeft: `4px solid #dc2626`, fontSize: '11px', fontWeight: '700', color: '#7f1d1d', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ fontSize: '14px', lineHeight: '14px' }}>🚀</span>
                      <div style={{ flex: 1 }}>
                        <div>⚠ ENCOMENDA NÃO PAGA · Promovida em {fmtDate(enc.promovido_pedido_em)} por confiança no cliente</div>
                        {enc.promovido_pedido_nota && <div style={{ fontWeight: 500, color: '#991b1b', fontStyle: 'italic', marginTop: '3px', fontSize: '10.5px' }}>"{enc.promovido_pedido_nota}"</div>}
                      </div>
                    </div>
                  )
                }

                let badge = null
                if (estado.tipo === 'falta') {
                  badge = (
                    <div style={{ padding: '6px 10px', backgroundColor: '#fef3c7', borderLeft: `3px solid #d97706`, fontSize: '11px', fontWeight: '700', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <AlertTriangle size={12} /> FALTA ENCOMENDAR · encomenda do cliente em {fmtDate(enc.created_at)}
                    </div>
                  )
                } else if (estado.tipo === 'enviado') {
                  badge = (
                    <div style={{ padding: '6px 10px', backgroundColor: '#e0f2fe', borderLeft: `3px solid #0284c7`, fontSize: '11px', fontWeight: '700', color: '#075985', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Send size={12} /> PEDIDO ENVIADO ao admin em {fmtDate(estado.em)} · aguarda factura
                    </div>
                  )
                } else if (estado.tipo === 'encomendada') {
                  const fact = estado.factura
                  const numF = fact?.numero_fatura_final || fact?.numero_fatura || enc.fornecedor_fatura_numero
                  const dataF = fact?.data_fatura_final || fact?.data_fatura
                  badge = (
                    <div style={{ padding: '6px 10px', backgroundColor: '#dcfce7', borderLeft: `3px solid #16a34a`, fontSize: '11px', fontWeight: '700', color: '#14532d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FileText size={12} /> JÁ ENCOMENDADA · Factura <span style={{ fontFamily: 'monospace' }}>{numF}</span>{dataF ? ` · ${fmtDate(dataF)}` : ''}
                    </div>
                  )
                }

                return (
                  <div key={enc.id} style={{ border: `1px solid ${sel ? C.primary : C.border}`, borderRadius: '5px', overflow: 'hidden', boxShadow: sel ? '0 0 0 2px rgba(10,10,10,0.08)' : 'none' }}>
                    {badgePromocao}
                    {badge}
                    <div style={{ padding: '10px 14px', backgroundColor: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        {podeSeleccionar && (
                          <input type="checkbox" checked={sel} onChange={() => toggleSel(enc.id)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }} />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span>{numero} <span style={{ color: C.textMuted, fontWeight: '500' }}>· {cliente}</span></span>
                            {custoPorEnc[enc.id] && (
                              <span title={custoPorEnc[enc.id].semCustoCount > 0 ? `${custoPorEnc[enc.id].semCustoCount} item(s) sem preço custo na BD - valor parcial` : 'Custo total desta encomenda (interno)'}
                                style={{ background: '#0a0a0a', color: '#FACC15', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <DollarSign size={10} /> {fmtEur(custoPorEnc[enc.id].custo)}
                                {custoPorEnc[enc.id].semCustoCount > 0 && <span style={{ color: '#fbbf24' }}> ⚠</span>}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
                            {fmtDate(enc.created_at)} · {itens.length} produto(s) · {totalUn} un · {enc.pago_2a_prestacao ? '2.ª paga' : '2.ª pendente'}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => copiar(enc.id, texto)}
                        style={{ height: '32px', padding: '0 12px', border: 'none', borderRadius: '3px', backgroundColor: foiCopiado ? C.success : C.primary, color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                        {foiCopiado ? <><Check size={13} /> Copiado</> : <><Copy size={13} /> Copiar</>}
                      </button>
                    </div>
                    <pre style={{ margin: 0, padding: '12px 14px', backgroundColor: C.surface, color: C.text, fontFamily: 'Consolas, "Courier New", monospace', fontSize: '12px', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {texto}
                    </pre>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div style={{ padding: '10px 18px', borderTop: `1px solid ${C.border}`, backgroundColor: C.bg, fontSize: '11px', color: C.textMuted, textAlign: 'center', flexShrink: 0 }}>
          {encomendas.length > 0 && `${encomendas.length} encomenda(s) com 1.ª prestação paga`}
        </div>
      </div>
    </div>
  )
}
